import { type MouseEvent, useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  List,
  ListOrdered,
  Palette,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Underline,
  Undo2,
  X,
} from "lucide-react";

const fonts = [
  { label: "Arial", value: "Arial" },
  { label: "Georgia", value: "Georgia" },
  { label: "Courier New", value: "Courier New" },
  { label: "Tahoma", value: "Tahoma" },
  { label: "Trebuchet", value: "Trebuchet MS" },
  { label: "Verdana", value: "Verdana" },
] as const;

const sizes = [
  { label: "Pequeño", value: "2" },
  { label: "Normal", value: "3" },
  { label: "Grande", value: "4" },
  { label: "Muy grande", value: "5" },
] as const;

const colors = ["#17283b", "#5b6f84", "#0a8189", "#1463d6", "#6d42c7", "#b33d49", "#c66a14", "#287a4b"];

export function RichEmailEditor({
  value,
  onHtmlChange,
  onTextChange,
  placeholder = "Escribí el correo. La firma configurada se agregará automáticamente.",
}: {
  value: string;
  onHtmlChange: (value: string) => void;
  onTextChange: (value: string) => void;
  placeholder?: string;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const linkAnchorRef = useRef<HTMLAnchorElement | null>(null);
  const [openMenu, setOpenMenu] = useState<"alignment" | "color" | null>(null);
  const [linkEditor, setLinkEditor] = useState({ open: false, url: "", error: "" });

  useEffect(() => {
    if (!editorRef.current || editorRef.current.innerHTML === value) return;
    editorRef.current.innerHTML = value;
  }, [value]);

  const syncEditor = () => {
    if (!editorRef.current) return;
    onHtmlChange(editorRef.current.innerHTML);
    onTextChange(editorRef.current.innerText.replace(/\n{3,}/g, "\n\n").trim());
  };
  const captureSelection = () => {
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const editor = editorRef.current;
    if (!range || !editor || !editor.contains(range.commonAncestorContainer)) return;
    savedRangeRef.current = range.cloneRange();
  };
  const restoreSelection = () => {
    const selection = window.getSelection();
    const range = savedRangeRef.current;
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };
  const runCommand = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(command, false, commandValue);
    captureSelection();
    syncEditor();
  };
  const openLinkEditor = (anchor?: HTMLAnchorElement | null) => {
    captureSelection();
    linkAnchorRef.current = anchor ?? null;
    setOpenMenu(null);
    setLinkEditor({ open: true, url: anchor?.getAttribute("href") ?? "", error: "" });
  };
  const applyLink = () => {
    const entered = linkEditor.url.trim();
    if (!entered) {
      setLinkEditor((current) => ({ ...current, error: "Ingresá una dirección." }));
      return;
    }
    const normalized = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entered)
      ? `mailto:${entered}`
      : /^(https?:\/\/|mailto:)/i.test(entered) ? entered : `https://${entered}`;
    if (!/^(https?:\/\/[^\s]+|mailto:[^\s@]+@[^\s@]+)$/i.test(normalized)) {
      setLinkEditor((current) => ({ ...current, error: "Ingresá una URL o un correo válido." }));
      return;
    }
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    restoreSelection();
    if (linkAnchorRef.current && editor.contains(linkAnchorRef.current)) {
      linkAnchorRef.current.setAttribute("href", normalized);
      linkAnchorRef.current.setAttribute("target", "_blank");
      linkAnchorRef.current.setAttribute("rel", "noopener noreferrer");
    } else {
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
      if (range?.collapsed) {
        const anchor = document.createElement("a");
        anchor.href = normalized;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.textContent = entered;
        range.insertNode(anchor);
        range.setStartAfter(anchor);
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      } else {
        document.execCommand("createLink", false, normalized);
        editor.querySelectorAll<HTMLAnchorElement>("a").forEach((anchor) => {
          if (anchor.getAttribute("href") === normalized) {
            anchor.target = "_blank";
            anchor.rel = "noopener noreferrer";
          }
        });
      }
    }
    captureSelection();
    syncEditor();
    linkAnchorRef.current = null;
    setLinkEditor({ open: false, url: "", error: "" });
  };
  const preserveSelection = (event: MouseEvent) => {
    event.preventDefault();
    captureSelection();
  };

  return <div className="rich-email-editor prospect-rich-email-editor">
    <div className="rich-email-toolbar" role="toolbar" aria-label="Formato del correo">
      <select className="rich-toolbar-select font-family" aria-label="Fuente" title="Fuente" defaultValue="" onMouseDown={captureSelection} onChange={(event) => { runCommand("fontName", event.target.value); event.currentTarget.value = ""; }}>
        <option value="" disabled>Sans Serif</option>
        {fonts.map((font) => <option key={font.value} value={font.value}>{font.label}</option>)}
      </select>
      <select className="rich-toolbar-select font-size" aria-label="Tamaño de fuente" title="Tamaño de fuente" defaultValue="" onMouseDown={captureSelection} onChange={(event) => { runCommand("fontSize", event.target.value); event.currentTarget.value = ""; }}>
        <option value="" disabled>Tamaño</option>
        {sizes.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}
      </select>
      <span />
      <button type="button" title="Negrita" aria-label="Negrita" onMouseDown={preserveSelection} onClick={() => runCommand("bold")}><Bold /></button>
      <button type="button" title="Cursiva" aria-label="Cursiva" onMouseDown={preserveSelection} onClick={() => runCommand("italic")}><Italic /></button>
      <button type="button" title="Subrayado" aria-label="Subrayado" onMouseDown={preserveSelection} onClick={() => runCommand("underline")}><Underline /></button>
      <button type="button" title="Color de texto" aria-label="Color de texto" aria-expanded={openMenu === "color"} onMouseDown={preserveSelection} onClick={() => setOpenMenu((current) => current === "color" ? null : "color")}><Palette /><ChevronDown className="toolbar-chevron" /></button>
      <button type="button" title="Alineación" aria-label="Alineación" aria-expanded={openMenu === "alignment"} onMouseDown={preserveSelection} onClick={() => setOpenMenu((current) => current === "alignment" ? null : "alignment")}><AlignLeft /><ChevronDown className="toolbar-chevron" /></button>
      <span />
      <button type="button" title="Lista numerada" aria-label="Lista numerada" onMouseDown={preserveSelection} onClick={() => runCommand("insertOrderedList")}><ListOrdered /></button>
      <button type="button" title="Lista con viñetas" aria-label="Lista con viñetas" onMouseDown={preserveSelection} onClick={() => runCommand("insertUnorderedList")}><List /></button>
      <button type="button" title="Reducir sangría" aria-label="Reducir sangría" onMouseDown={preserveSelection} onClick={() => runCommand("outdent")}><IndentDecrease /></button>
      <button type="button" title="Aumentar sangría" aria-label="Aumentar sangría" onMouseDown={preserveSelection} onClick={() => runCommand("indent")}><IndentIncrease /></button>
      <button type="button" title="Cita" aria-label="Cita" onMouseDown={preserveSelection} onClick={() => runCommand("formatBlock", "blockquote")}><Quote /></button>
      <button type="button" title="Tachado" aria-label="Tachado" onMouseDown={preserveSelection} onClick={() => runCommand("strikeThrough")}><Strikethrough /></button>
      <button type="button" title="Agregar o editar enlace" aria-label="Agregar o editar enlace" aria-expanded={linkEditor.open} onMouseDown={preserveSelection} onClick={() => openLinkEditor()}><Link2 /></button>
      <span />
      <button type="button" title="Deshacer" aria-label="Deshacer" onMouseDown={preserveSelection} onClick={() => runCommand("undo")}><Undo2 /></button>
      <button type="button" title="Rehacer" aria-label="Rehacer" onMouseDown={preserveSelection} onClick={() => runCommand("redo")}><Redo2 /></button>
      <button type="button" title="Quitar formato" aria-label="Quitar formato" onMouseDown={preserveSelection} onClick={() => runCommand("removeFormat")}><RemoveFormatting /></button>
      {openMenu === "color" && <div className="rich-toolbar-popover color-picker" role="menu" aria-label="Color de texto">{colors.map((color) => <button key={color} type="button" role="menuitem" aria-label={`Color ${color}`} style={{ backgroundColor: color }} onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("foreColor", color); setOpenMenu(null); }} />)}</div>}
      {openMenu === "alignment" && <div className="rich-toolbar-popover alignment-picker" role="menu" aria-label="Alineación del texto">
        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyLeft"); setOpenMenu(null); }}><AlignLeft />Izquierda</button>
        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyCenter"); setOpenMenu(null); }}><AlignCenter />Centro</button>
        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyRight"); setOpenMenu(null); }}><AlignRight />Derecha</button>
        <button type="button" role="menuitem" onMouseDown={(event) => event.preventDefault()} onClick={() => { runCommand("justifyFull"); setOpenMenu(null); }}><AlignJustify />Justificar</button>
      </div>}
      {linkEditor.open && <div className="rich-link-popover" role="dialog" aria-label="Editar enlace">
        <Link2 />
        <div><input autoFocus value={linkEditor.url} placeholder="Escribí o pegá un vínculo" aria-label="URL del enlace" onChange={(event) => setLinkEditor({ open: true, url: event.target.value, error: "" })} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyLink(); } if (event.key === "Escape") setLinkEditor({ open: false, url: "", error: "" }); }} />{linkEditor.error && <small>{linkEditor.error}</small>}</div>
        <button type="button" className="link-apply" onClick={applyLink}>Aplicar</button>
        <button type="button" className="link-cancel" aria-label="Cancelar enlace" onClick={() => { linkAnchorRef.current = null; setLinkEditor({ open: false, url: "", error: "" }); }}><X /></button>
      </div>}
    </div>
    <div ref={editorRef} className="rich-email-content" contentEditable suppressContentEditableWarning role="textbox" aria-label="Mensaje" aria-multiline="true" data-placeholder={placeholder} onKeyUp={captureSelection} onMouseUp={captureSelection} onInput={syncEditor} onClick={(event) => { const anchor = (event.target as HTMLElement).closest("a"); if (anchor instanceof HTMLAnchorElement) { event.preventDefault(); openLinkEditor(anchor); } }} />
  </div>;
}
